import sumBy from 'lodash/sumBy';
import { atom, AtomEffect, DefaultValue, selector } from 'recoil';
import { ResourcesToProduct } from 'types/resources-to-products';
import { Membership } from '../types/membership';
import { MembershipItemInCart } from '../types/membership-item-in-cart';
import { CheckoutStore } from 'types/checkout';
import { Product } from 'types/product';
import { localStorage } from 'lib/storage';
import { Answer } from 'types/answer';
import { ProductInCartEnum } from 'types/cart/productInCart';
import { calculateTotalPriceWithTax } from '../lib/price';

const CHECKOUT_STORAGE_KEY = 'checkoutState';

/**
 * This is really just a copy paste of programCheckoutStore.ts
 * @todo - remove copy paste
 */

export interface MembershipCheckoutStore extends CheckoutStore {
	// The Membership the user selected in the landing page Search module
	selectedMembership?: Membership;

	selectedProduct?: Product;

	selectedResources?: ResourcesToProduct;

	/* eslint-disable @typescript-eslint/no-explicit-any*/
	selectedAnswersForInit?: any;
}
/**
 * @todo remove copy paste - this is exactly like in program
 * @param key
 */
const localStorageEffect = (key: string): AtomEffect<MembershipCheckoutStore> => ({ setSelf, onSet }) => {
	const savedValue = localStorage.getItem(key);

	if (savedValue !== null) {
		setSelf(savedValue);
	}

	onSet(newValue => {
		if (newValue instanceof DefaultValue) {
			localStorage.removeItem(key);
		}

		localStorage.setItem(key, newValue);
	});
};

const checkout = atom<MembershipCheckoutStore>({
	key: 'membershipCheckout',
	default: {},
	// Based on:
	// https://recoiljs.org/docs/guides/atom-effects/#local-storage-persistence
	effects_UNSTABLE: [localStorageEffect(CHECKOUT_STORAGE_KEY)],
});

const checkoutCartItems = selector<MembershipItemInCart[]>({
	key: 'membershipCheckoutCartItems',
	get: ({ get }) => {
		const checkoutState = get(checkout);
		const { selectedMembership } = checkoutState;

		if (!selectedMembership) {
			return [];
		}

		/* eslint no-useless-concat: "error"*/

		// let firstName: string[] = [];
		// if (Array.isArray(checkoutState.selectedFamilyMember)) {
		//   firstName = checkoutState.selectedFamilyMember.map(familyMember => {
		//     return familyMember.firstName || '';
		//   });
		// }

		const items: MembershipItemInCart[] = [
			{
				ordinal: 1,
				// parentOrdinal?: number;
				type: ProductInCartEnum.PACKAGE,
				product: selectedMembership.package.parentProduct,
				member: Array.isArray(checkoutState.selectedFamilyMember)
					? checkoutState.selectedFamilyMember[0]
					: checkoutState.selectedFamilyMember,
				// addons?: Product[],
				answers: Array.isArray(checkoutState.selectedFamilyMember) ? undefined : checkoutState.selectedAnswers,
				membership: selectedMembership,
				resources: checkoutState.selectedResources,
			},
		];
		selectedMembership.package.children.map((childProduct, index) => {
			if (Array.isArray(checkoutState?.selectedFamilyMember) && checkoutState?.selectedFamilyMember[index]) {
				const answers: { userId: number; answers: Answer[] }[] = [];
				(checkoutState.selectedAnswers as {
					userId: number;
					answers: Answer[];
				}[])?.forEach((answer: { userId: number; answers: Answer[] }) => {
					if (
						Array.isArray(checkoutState?.selectedFamilyMember) &&
						answer.userId === checkoutState?.selectedFamilyMember[index].id
					) {
						answers.push(answer);
					}
					return answer;
				});

				items.push({
					ordinal: 2 + index,
					// parentOrdinal?: number;
					type: ProductInCartEnum.ADDON,
					product: childProduct.product,
					member: Array.isArray(checkoutState.selectedFamilyMember)
						? checkoutState?.selectedFamilyMember[index]
						: checkoutState.selectedFamilyMember,
					// addons?: Product[],
					answers: answers,
					membership: selectedMembership,
					resources: checkoutState.selectedResources,
				});
			}
			return childProduct;
		});

		return items;
	},
});

const checkoutCartBookingFee = selector<number>({
	key: 'membershipCheckoutCartBookingFee',
	get: () => {
		return 0;
	},
});

const checkoutCartTotalPrice = selector<number>({
	key: 'membershipCheckoutCartTotalPrice',
	get: ({ get }) => {
		const checkoutCartItemsState = get(checkoutCartItems);

		return sumBy(checkoutCartItemsState, ({ product }) => {
			return calculateTotalPriceWithTax(product?.currPrice?.price ?? 0, product?.isTaxInclusive, product?.tax);
		});
	},
});

const checkoutEventApplications = selector({
	key: 'membershipCheckoutEventApplications',
	get: ({ get }) => {
		const checkoutState = get(checkout);

		return checkoutState?.selectedAnswersForInit ?? {};
	},
});

export const checkoutStore = {
	checkout,
	checkoutCartItems,
	checkoutCartBookingFee,
	checkoutCartTotalPrice,
	checkoutEventApplications,
};
